// dependencies
import { ObjectId } from "mongodb";
import React, { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { ICustomQuestionGetResponse } from "@performyard/models";

// components
import { AdminTitle } from "../../../../../components/Admin";
import Button from "../../../../../components/Button";
import TextInput from "../../../../../components/Inputs/InputText";
import SelectInput from "../../../../../components/Inputs/SelectInput";
import ConfirmationModal from "../../../../../components/Modal/Templates/ConfirmationModal";
import PageTitle from "../../../../../components/PageTitle";
import Spacer from "../../../../../components/Spacer";
import Table from "../../../../../components/TableLite";
import Typography from "../../../../../components/Text";
import CreateOrEditCustomSurveyQuestionModal from "./components/CreateOrEditCustomSurveyQuestionModal";
import MaxQuestionsReachedModal from "./components/MaxQuestionsReachedModal";
import getSurveyQuestionsTableCols from "./components/SurveyQuestionsTableCols";

// hooks
import { SearchServiceSort } from "../../../../../api/hooks";
import { useEngagementSurveyQuestions, useMutateBuiltInQuestionStatus } from "../../../../../api/hooks/defs/engagement";
import {
	useDeleteEngagementSurveyCustomQuestion,
	useMutateEngagementSurveyCustomQuestion,
} from "../../../../../api/hooks/defs/engagement-survey-custom-questions";
import useToast from "../../../../../hooks/useToast";

// utils
import { processQuestions } from "./utils";

// config
import Config from "../../../../Engagement/Config";
// css
import css from "./SurveyQuestions.module.scss";

const SurveyQuestions = () => {
	const toast = useToast();

	// when this state is set, the delete modal will appear
	const [deleteModalQuestion, setDeleteModalQuestion] = useState<ObjectId | null>();
	const [editModalQuestion, setEditModalQuestion] = useState<ICustomQuestionGetResponse | null>(null);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [currentSort, setCurrentSort] = useState<SearchServiceSort>({
		name: "factor",
		direction: "asc",
	});
	const [search, setSearch] = useState("");
	const [searchValue] = useDebounceValue(search, 250);
	const [factor, setSelectedFactor] = useState("All Factors");

	const { isLoading, refetch, data } = useEngagementSurveyQuestions();

	const getFactors = () => {
		return [...Config.FACTORS.FACTORS, "Custom"];
	};

	const maxAllowedCustomQuestions = data?.maxAllowedCustomQuestions || 0;

	const customQuestionsCount = data?.customQuestionsCount || 0;

	const questions = useMemo(() => {
		let questions = [];
		if (data?.results) {
			questions = processQuestions(data.results, factor, currentSort, searchValue);
		}
		return questions;
	}, [data?.results, currentSort.name, currentSort.direction, factor, searchValue]);

	const { mutate: updateCustomQuestion } = useMutateEngagementSurveyCustomQuestion(refetch, () =>
		toast("Failed to update Custom Survey Question", {
			type: "error",
			size: "large",
		})
	);

	const { mutate: remove } = useDeleteEngagementSurveyCustomQuestion(refetch, () =>
		toast("Failed to remove Custom Survey Question", {
			type: "error",
			size: "large",
		})
	);

	const { mutate: updateBuiltInQuestionStatus } = useMutateBuiltInQuestionStatus(refetch, () =>
		toast("Failed to update Survey Question", {
			type: "error",
			size: "large",
		})
	);

	const handleOptionsDrawer = async (option: { id: string; label: string }, question: ICustomQuestionGetResponse) => {
		const updateQuestion = (updateObject) => {
			if (question._id) {
				updateCustomQuestion({ _id: question._id, ...updateObject });
			} else {
				updateBuiltInQuestionStatus({ id: question.id, ...updateObject });
			}
		};

		switch (option.id) {
			case "delete":
				setDeleteModalQuestion(question._id);
				break;

			case "disable":
				updateQuestion({ enabled: false });
				break;

			case "enable":
				updateQuestion({ enabled: true });

				break;

			case "edit":
				setEditModalQuestion(question);
				break;

			default:
				break;
		}
	};

	const resetModals = () => {
		setEditModalQuestion(null);
		setIsCreateModalOpen(false);
	};

	const deleteModal = deleteModalQuestion ? (
		<ConfirmationModal
			positiveAction={() => {
				remove(deleteModalQuestion);
				setDeleteModalQuestion(null);
			}}
			negativeAction={() => {
				setDeleteModalQuestion(null);
			}}
			positiveActionLabel="Delete"
			negativeActionLabel="Cancel"
		>
			<Typography size="paragraph-1" weight="semi-bold">
				Are you sure you want to delete this question?
			</Typography>
		</ConfirmationModal>
	) : null;

	const factors = getFactors();

	const dropdownFactors = [
		{ label: "All Factors", value: "All Factors" },
		...factors.sort().map((factor) => ({
			label: factor,
			value: factor,
		})),
	];

	const onFactorChanged = (factor) => {
		setSelectedFactor(factor.value);
	};

	const handleColumnClick = (columnsSort) => {
		let sortDirection = "asc";
		if (currentSort?.name === columnsSort) {
			sortDirection = currentSort?.direction === "asc" ? "desc" : "asc";
		}
		setCurrentSort({
			name: columnsSort,
			direction: sortDirection as "asc" | "desc",
		});
	};

	return (
		<div className={css.main}>
			<AdminTitle>Questions</AdminTitle>
			<PageTitle title="Questions" />
			<Spacer height={4} />
			<div className={css.tableControls}>
				<TextInput
					className={css.search}
					value={search}
					onChange={setSearch}
					placeholder="Search fields..."
					icon={faSearch}
					clearable
				/>
				<div className={css.headerRight}>
					<div className={css.factorFilter}>
						<label htmlFor="factor-filter">
							<Typography size="caption" weight="semi-bold">
								Filter by Factor
							</Typography>
						</label>
						<div className={css.select}>
							<SelectInput
								inputId="factor-filter"
								results={dropdownFactors}
								onChange={onFactorChanged}
								value={factor}
								isSearchable={false}
								className={css.selectInput}
							/>
						</div>
					</div>
					<Button
						onClick={() => setIsCreateModalOpen(true)}
						className={css.createQuestionButton}
						color="blue"
						icon={faPlus}
						iconPosition="left"
					>
						Custom Question
					</Button>
				</div>
			</div>
			<Spacer height={2} />
			<div className={css.table}>
				<Table
					theme="vJan23"
					rowKey="question"
					columns={getSurveyQuestionsTableCols(handleOptionsDrawer)}
					rows={questions}
					onColumnClick={handleColumnClick}
					isLoading={isLoading}
					activeSort={currentSort?.name}
					activeSortDirection={currentSort?.direction}
					emptyChildren={
						searchValue ? (
							<div className={css.emptyState}>
								<Typography size="paragraph-1" weight="semi-bold">
									There are no results that match your search.
								</Typography>
							</div>
						) : (
							<div className={css.emptyState}>
								<Typography size="paragraph-1" weight="semi-bold">
									You currently have no custom questions.
								</Typography>
								<Typography size="paragraph-1" weight="semi-bold">
									Create a custom question to begin.
								</Typography>
								<Spacer height={1} />
								<Button
									onClick={() => setIsCreateModalOpen(true)}
									className={css.createQuestionButton}
									color="blue"
									icon={faPlus}
									iconPosition="left"
								>
									Custom Question
								</Button>
							</div>
						)
					}
				/>
			</div>
			{deleteModal}
			<CreateOrEditCustomSurveyQuestionModal
				isOpen={
					editModalQuestion !== null ||
					(isCreateModalOpen && customQuestionsCount < maxAllowedCustomQuestions)
				}
				onClose={resetModals}
				onSave={() => {
					resetModals();
					refetch();
				}}
				selectedQuestion={editModalQuestion || undefined}
			/>
			<MaxQuestionsReachedModal
				isOpen={customQuestionsCount >= maxAllowedCustomQuestions && isCreateModalOpen}
				maxAllowedQuestions={maxAllowedCustomQuestions}
				onClose={resetModals}
			/>
		</div>
	);
};

export default SurveyQuestions;
